import { useEffect } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { t } from 'i18next'
import { FormProvider, useForm } from 'react-hook-form'

import { stepsFlow } from '@/features/proposta/entities/Comprador/steps'
import { ProcuradorUseCase } from '@/features/proposta/entities/Comprador/use-case/procurador-use-case'
import type { TClienteCompleto } from '@/features/proposta/hooks/use-orquestrar-criacao-edicao'
import { useStepManager } from '@/features/proposta/hooks/use-stepper-proposta'
import type { TCompradoresCadastrados } from '@/features/proposta/interfaces/proposta.type'
import type { TProcuradorPropostaSchema, TPropostaSchemaInput } from '@/features/proposta/schemas'
import {
  type CompradorVinculoStepSchemaInput,
  type CompradorVinculoStepSchemaOutput,
} from '@/features/proposta/schemas/compradores/comprador-pf.schema'
import { StepCompradorLayout } from '@/pages/proposta/etapa-comprador/cadastro-comprador/components/stepper-comprador'
import { ContatosComprador } from '@/pages/proposta/etapa-comprador/cadastro-comprador/contatos-comprador'
import { DadosProcuradorVinculo } from '@/pages/proposta/etapa-comprador/cadastro-comprador/dados-procurador-vinculo'
import { EstadoCivilVinculo } from '@/pages/proposta/etapa-comprador/cadastro-comprador/estado-civil-vinculo'
import { SelecaoProcuradorReutilizar } from '@/pages/proposta/etapa-comprador/cadastro-comprador/selecao-procurador-reutilizar'
import { VincularCompradorAoProcurador } from '@/pages/proposta/etapa-comprador/cadastro-comprador/vincular-comprador-ao-procurador'
import type { TNacionalidade, TProfissao } from '@/types/cliente'

type TCadastroProcuradorFluxoIndependenteProps = {
  onCancel: () => void
  onSubmit: (data: CompradorVinculoStepSchemaOutput, isDirty: boolean) => Promise<void>
  clientes: TPropostaSchemaInput['cadastroCompradores']['clientes']
  compradores: TCompradoresCadastrados
  procuradores: TProcuradorPropostaSchema[]
  nacionalidades: TNacionalidade[]
  profissoes: TProfissao[]
  procuradorToEdit: TClienteCompleto | null
  emailsJaVinculados: Record<string, string>
}

export const CadastroProcuradorFluxoIndependente = ({
  onCancel,
  onSubmit,
  clientes,
  procuradores,
  compradores,
  nacionalidades,
  profissoes,
  procuradorToEdit,
  emailsJaVinculados,
}: TCadastroProcuradorFluxoIndependenteProps) => {
  const { schema, conditionalSteps } = stepsFlow.procurador
  const form = useForm<CompradorVinculoStepSchemaInput>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: ProcuradorUseCase.iniciarDefaultValues(emailsJaVinculados, nacionalidades[0]?.id),
  })

  const isDirty = form.formState.isDirty
  const { steps, idx, onNext, onFinish, onBack, isLast } = useStepManager(
    conditionalSteps,
    () => form.getValues(),
    onCancel,
    onSubmit,
    isDirty,
    compradores,
    procuradores,
  )
  const currentStep = steps[idx]

  useEffect(() => {
    if (procuradorToEdit) {
      const procurador = procuradores.find((p) => p.procuradorId === procuradorToEdit.id)
      form.reset({
        emailsJaVinculados,
        compradorVinculadoIds: procurador?.clientesIds,
        contato: {
          email: procuradorToEdit.email,
          telefone: procuradorToEdit.telefone,
        },
        estadoCivil: {
          regimeCasamento: procuradorToEdit.regimeCasamento,
          uniaoEstavel: procuradorToEdit.uniaoEstavel,
          status: procuradorToEdit.estadoCivil,
          tipoAssinatura: null,
        },
        dados: {
          buscouCpf: true,
          cpf: procuradorToEdit.documento,
          nome: procuradorToEdit.nome,
          nacionalidadeId: procuradorToEdit.nacionalidadeId,
          profissaoId: procuradorToEdit.profissaoId,
          maiorDe18Anos: procuradorToEdit.maiorDe18Anos ? 'sim' : 'nao',
          email: procuradorToEdit.email,
          id: procuradorToEdit.id,
        },
        imagem: procuradorToEdit.imagem,
        mode: 'edit',
        tipo: 'Procurador',
        tipoDoCliente: 'Contato',
      })
    }
  }, [])

  const renderContent = () => {
    switch (currentStep) {
      case 'escolherClienteExistente':
        return (
          <SelecaoProcuradorReutilizar
            compradores={compradores}
            clientes={clientes}
            procuradores={procuradores}
            onBack={onBack}
            onNext={onNext}
          />
        )
      case 'dadosProcurador':
        return (
          <DadosProcuradorVinculo
            procuradores={procuradores}
            compradores={compradores}
            nacionalidades={nacionalidades}
            onBack={onBack}
            onNext={onNext}
            profissoes={profissoes}
          />
        )
      case 'contatosProcurador':
        return (
          <ContatosComprador
            titulo={t('proposta:cadastro_compradores.steps.contatos.procurador_titulo')}
            fieldName="contato"
            isLast={isLast}
            onBack={onBack}
            onNext={onNext}
            onFinish={(value) => onFinish(value as CompradorVinculoStepSchemaOutput)}
          />
        )
      case 'estadoCivilProcurador':
        return (
          <EstadoCivilVinculo
            fieldPath="estadoCivil"
            isLast={isLast}
            onBack={onBack}
            onNext={onNext}
            onFinish={(value) => onFinish(value as CompradorVinculoStepSchemaOutput)}
          />
        )
      case 'vincularCompradorParaProcurador':
        return (
          <VincularCompradorAoProcurador
            procuradores={procuradores}
            compradores={compradores}
            onBack={onBack}
            onFinish={onFinish}
          />
        )
      default:
        return null
    }
  }

  return (
    <FormProvider {...form}>
      <StepCompradorLayout steps={steps} current={idx} onNext={onNext} onBack={onBack} onCancel={onCancel}>
        {renderContent()}
      </StepCompradorLayout>
    </FormProvider>
  )
}
